import { useCallback, useState } from "react";
import { toast } from "sonner";

import {
	blockingWarnings,
	delayedWarnings,
	describeSendWarning,
	type SendWarning,
} from "./send-warnings";

/**
 * HA-619 — presents fire-time print warnings for every send path (the Send button and a course
 * fire), so both behave identically.
 *
 * The split is the point: a queue with no printer gets a modal the operator has to dismiss,
 * because nothing will print and somebody has to walk the order over. A queue whose printers are
 * merely offline gets a persistent, dismissible toast — the broker holds the job and it prints on
 * reconnect, so interrupting service for it would train staff to click through warnings.
 */
export function useSendWarnings() {
	const [blocking, setBlocking] = useState<SendWarning[]>([]);

	const report = useCallback((warnings: SendWarning[]) => {
		if (warnings.length === 0) return;

		for (const warning of delayedWarnings(warnings)) {
			toast.warning(describeSendWarning(warning), {
				// Persistent: it stays until dismissed, so it cannot scroll past unread mid-service.
				duration: Infinity,
				dismissible: true,
			});
		}

		const mustAcknowledge = blockingWarnings(warnings);
		if (mustAcknowledge.length > 0) setBlocking(mustAcknowledge);
	}, []);

	const acknowledge = useCallback(() => setBlocking([]), []);

	return { blocking, report, acknowledge };
}

export type SendWarningsPresenter = ReturnType<typeof useSendWarnings>;
